/**
 * Primitivas visuais compartilhadas das telas analíticas do P4
 * (KPIs PPCP/DBR e Análise de Atrasos). Tudo via tokens do tema —
 * dark mode automático; nenhuma cor hardcoded fora do mapa semântico.
 */
import styled, { keyframes } from "styled-components";

/** Números/horas sempre em mono (padrão F.2 das telas v2). */
export const Mono = styled.span`
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
`;

/**
 * Tabela canônica das telas v2: table-layout fixed + ellipsis em toda
 * célula (zero scroll lateral — princípio constitucional). Larguras
 * via <colgroup> somando 100%.
 */
export const TabelaFixa = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-3);
    padding: 8px 10px;
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  td {
    padding: 8px 10px;
    border-top: 1px solid var(--border-subtle);
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tbody tr[data-clicavel="true"] {
    cursor: pointer;
  }
  tbody tr[data-clicavel="true"]:hover {
    background: var(--surface-hover);
  }
`;

const brilho = keyframes`
  0% { opacity: 0.45; }
  50% { opacity: 1; }
  100% { opacity: 0.45; }
`;

/** Placeholder de carregamento (altura fixa evita layout-shift). */
export const Shimmer = styled.div`
  height: ${({ $h = 96 }) => $h}px;
  border-radius: 12px;
  background: var(--surface-2);
  animation: ${brilho} 1.4s ease-in-out infinite;
`;

/** Grid de mini-stats (label uppercase + valor mono) dos blocos Tambor/Corda. */
export const MiniStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
`;

export const MiniStat = styled.div`
  background: var(--surface-2);
  border-radius: 10px;
  padding: 10px 12px;

  .lbl {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-3);
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .val {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
  }
  .meta {
    font-size: 11px;
    color: var(--ink-3);
    margin-top: 2px;
  }
`;

export const LinhaErro = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

/** Barra de ferramentas sob o EntityHeader (janela/lente + ExportMenu). */
export const BarraFerramentas = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin: -6px 0 14px;
`;

/** Nota discreta (fonte de dado, parâmetros read-only, avisos honestos). */
export const NotaDiscreta = styled.p`
  font-size: 11px;
  color: var(--ink-4);
  margin: 8px 0 0;
`;

/** Título de sub-bloco (uppercase 11px neutral, padrão do mockup). */
export const TituloBloco = styled.h4`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-3);
  margin: 0 0 8px;
`;

/** Formata número pt-BR com casas decimais controladas. */
export function fmtNum(valor, casas = 1) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: casas,
  });
}

/** Percentual pt-BR ("96,2%"); null/NaN -> "—" (nunca número inventado). */
export function fmtPct(valor, casas = 1) {
  const n = Number(valor);
  if (valor === null || valor === undefined || !Number.isFinite(n)) return "—";
  return `${fmtNum(n, casas)}%`;
}
